import { Dialog, DialogContent, DialogActions, Grid, Typography, Switch } from "@mui/material";
import { DialogHeader } from "../../../auth/components/DialogHeader";
import BaseButton from "../../../main/components/BaseButton";
import DangerBaseButton from "../../../main/components/DangerBaseButton";
import { useContext, useEffect, useState } from "react";
import { MainContext } from "../../../main/context/MainContextProvider";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import SelectInput from "../../../main/components/SelectInput";
import NumberInput from "../../../main/components/NumberInput";
import { commandsApi } from "../../../services/api";
import { CommandValue, ExexuteCommand } from "../types";
import { ActionAlert } from "../../../main/components/alerts/ActionAlert";
import { ConfirmationModal } from "../../../main/components/ConfirmationModal";

const schema = yup.object().shape({
    commandType: yup.string().oneOf(['integer', 'real', 'boolean', 'trigger']).required(),
    commandName: yup.string().required("O comando é obrigatório"),
    CommandValue: yup.lazy((value, options) => {
        if (options.parent.commandType === "trigger") {
            return yup.mixed().notRequired();
        }
        if (typeof value === "boolean") {
            return yup.boolean().required("O valor é obrigatório");
        }
        if (typeof value === "number") {
            return yup.number().required("O valor é obrigatório");
        }
        return yup.mixed().required("O valor é obrigatório");
    })
});

export type CommandsData = yup.InferType<typeof schema>;

export type CommandsModalProps = {
    open: boolean;
    onClose: () => void;
    type: 'integer' | 'real' | 'boolean' | 'trigger';
    commands: string[];
    areaCode: string;
    equipmentIdentifier?: string;
    usinaCode: string
    usinaId: string;
    statusLabel?: React.ReactNode;
}

export default function CommandsModal(props: CommandsModalProps) {
    const targetName = props.equipmentIdentifier
        ? `${props.usinaCode}/${props.areaCode}/${props.equipmentIdentifier}`
        : `${props.usinaCode}/${props.areaCode}`;

    const {
        control,
        formState: { errors },
        handleSubmit,
        setValue,
    } = useForm({
        resolver: yupResolver(schema),
        defaultValues: {
            commandType: props.type,
            commandName: "",
            CommandValue: null,
        }
    });

    const { open, onClose, type, commands, areaCode, equipmentIdentifier, usinaCode, usinaId } = props;

    const { theme } = useContext(MainContext);
    const primary = theme.muiTheme.palette.primary.main;

    const commandOptions = commands?.map((command) => ({
        label: command ?? "",
        value: command,
    }));

    const [showSuccesModal, setShowSuccessModal] = useState(false)
    const [showErrorModal, setShowErrorModal] = useState(false)
    const [isSaveConfirmationOpen, setSaveConfirmationOpen] = useState(false);

    const [currentValue, setCurrentValue] = useState<any>(null);

    const [loading, setLoading] = useState(false);

    const onConfirm = async (data: CommandsData) => {
        const getCommandValue = (): CommandValue => {
            switch (type) {
                case "boolean":
                    return {
                        boolValue: Boolean(data.CommandValue),
                        integerValue: undefined,
                        realValue: undefined,
                    };
                case "integer":
                    return {
                        boolValue: undefined,
                        integerValue: Number(data.CommandValue),
                        realValue: undefined,
                    };
                case "real":
                    return {
                        boolValue: undefined,
                        integerValue: undefined,
                        realValue: Number(data.CommandValue),
                    };
                default:
                    return {
                        boolValue: undefined,
                        integerValue: undefined,
                        realValue: undefined,
                    };
            }
        };

        setLoading(true);
        try {
            const postData: ExexuteCommand = {
                usinaId,
                areaCode: areaCode,
                usinaCode: usinaCode,
                equipmentIdentifier: equipmentIdentifier,
                commandName: data.commandName,
                commandType: type,
                commandValue: getCommandValue(),
            }

            await commandsApi.executeCommand(postData);

        } catch (error) {
            setShowErrorModal(true)
        } finally {
            setLoading(false);
            onClose();
        }
    }

    const getValueComponent = () => {
        switch (type) {
            case 'boolean':
                return (
                    <Grid>
                        <Typography variant="subtitle1" >
                            Estado do comando
                        </Typography>
                        Desligar
                        <Switch
                            checked={currentValue}
                            onChange={(_, checked) => {
                                setCurrentValue(checked);
                            }}
                        />
                        Ligar
                    </Grid>
                );

            case 'integer':
            case 'real':
                return (
                    <>
                        <NumberInput
                            sx={{
                                width: "100%",
                                maxWidth: "280px",
                            }}
                            control={control}
                            fieldName={`CommandValue`}
                            label="Valor"
                            errors={errors}
                        />
                    </>
                );

            default:
                return <></>;
        }
    };

    useEffect(() => {
        setValue(`CommandValue`, currentValue);
    }, [currentValue]);

    const onSubmit = () => {
        setSaveConfirmationOpen(true);
    };

    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                aria-labelledby="customized-dialog-title"
                fullWidth
                maxWidth="sm"
            >
                <DialogHeader id="customized-dialog-title" onClose={onClose}>
                    Comando - {targetName}
                </DialogHeader>
                <DialogContent

                    dividers
                >
                    <Grid onSubmit={handleSubmit(onSubmit)} component={"form"}>
                        <Grid display={"flex"} item xs={12} sx={{ justifyContent: "center" }}>
                            <SelectInput
                                fieldName="commandName"
                                label="Comando"
                                control={control}
                                errors={errors}
                                options={commandOptions ?? []}
                                sx={{ width: "100%", marginBottom: "30px", marginRight: "10px" }}
                            />
                        </Grid>
                        <Grid
                            display={"flex"}
                            item
                            xs={12}
                            sx={{ justifyContent: "center" }}
                        >
                            {getValueComponent()}
                        </Grid>

                        {props.statusLabel && (
                            <Grid item xs={12}>
                                {props.statusLabel}
                            </Grid>
                        )}

                        <DialogActions sx={{ marginTop: "24px" }}>
                            <DangerBaseButton onClick={onClose} title="Cancelar" />
                            <BaseButton
                                loading={loading}
                                sx={{
                                    backGroundColor: primary,
                                    borderColor: primary,
                                    height: "34px",
                                    width: "95px",
                                    "& .MuiTypography-root": {
                                        color: primary,
                                    },
                                    "&:hover": {
                                        borderColor: primary,
                                        backgroundColor: primary,
                                        color: primary,
                                        "& .MuiTypography-root": {
                                            color: "#FFFFFF",
                                        },
                                    },
                                }}
                                title="Confirmar"
                                type="submit"
                            />
                        </DialogActions>
                    </Grid>
                </DialogContent>
            </Dialog>
            <ConfirmationModal
                open={isSaveConfirmationOpen}
                setOpen={() => setSaveConfirmationOpen(false)}
                title="Executar Comando"
                contentText="Deseja confirmar a execução do comando?"
                onConfirm={handleSubmit(onConfirm)}
                setOpenErrorModal={setShowErrorModal}
                setOpenSuccessModal={setShowSuccessModal}
            />
            <ActionAlert
                message={`Comando realizado com sucesso`}
                severity="success"
                open={showSuccesModal}
                setOpen={setShowSuccessModal}
                onClose={onClose}
            />
            <ActionAlert
                message={`Erro ao enviar comando`}
                severity="error"
                open={showErrorModal}
                setOpen={setShowErrorModal}
                onClose={onClose}
            />
        </>
    )
}